import React, { useState, useRef, useEffect } from 'react';
import { debounce } from 'throttle-debounce'
import { runByFeature } from './utils/runByFeature'

import Loader from './utils/ScriptLoader';
import './styles/htmlHeneric.scss';

const hasJQuery = () => !!window.$
const checkTypeAndConvert = (value, type) => value && (type === 'number') ? value && isNaN(Number(value)) ? -1 : Number(value) : value

const HtmlGeneric = ({value, options, onChange, schema, onBlur, id, features}) => {
  const [currentValue, setValue] = useState(value || '')
  const container = useRef(null)
  const changeTimeout = useRef(null)
  const parser = new DOMParser()
  const { custom : { objectText, callbackFunctionName }} = options
  const type = ((schema.minimum) || (schema.maximum))
    ? 'number'
    : schema.type
      ? schema.type
      : 'string'

  useEffect(() => {
    const run = async () => {
      window.DialValues = window.DialValues || {}
      window.DialValues[id] = value
      if (!window.getDialValue) {
        window.getDialValue = (id, original) => original.replaceAll('#DIAL_VALUE#', window.DialValues[id])
      }

      if (objectText) {
        const dom = parser.parseFromString(objectText, 'text/html')
        const scripts = dom.querySelectorAll('script')
        const stylesheetLinks = dom.querySelectorAll('link[rel="stylesheet"]');
        const internalStyles = dom.querySelectorAll('style');

        if(!hasJQuery()) {
          const loader = new Loader({
            src: 'https://code.jquery.com/jquery-3.6.0.min.js',
            global: 'body'
          })

          await loader.load()
        }

        for (let script of scripts) {
          if (script.src) {
            const loader = new Loader({
              src: script.src,
              global: 'body'
            })
            await loader.load()
          } else {
            const cloned = document.createElement('script')
            cloned.type = script.type
            cloned.innerHTML = script.innerHTML
              .replaceAll('#DIAL_DISPLAY_NAME#', schema.title)
              .replace(/([`'"])(\s|.)*?(\1)/gm, ((match, p1) => match.replaceAll('#DIAL_VALUE#', `${p1} + getDialValue('${id}', '#DIAL_VALUE#') + ${p1}`)))
            document.body.appendChild(cloned)
          }
          script.remove()
        }

        for (let style of internalStyles) {
          const clonedStyle = document.createElement('style')
          clonedStyle.innerHTML = style.innerHTML
          document.head.appendChild(clonedStyle);
          style.remove()
        }

        for (let link of stylesheetLinks) {
          const clonedLink = document.createElement('link')
          clonedLink.href = link.href
          clonedLink.rel = link.rel
          document.head.appendChild(clonedLink);
          link.remove()
        }

        if (container && container.current) {
          container.current.innerHTML = dom.body.innerHTML
            .replace(/#DIAL_DISPLAY_NAME#/gm, schema.title)
            .replace(/(<.*?\s+[\w]+=".*)(#DIAL_VALUE#)(.*")/gm, `$1${currentValue}$3 data-xmpie-value="${currentValue}"`)
            .replace(/<(\w+)(.*?>)(\s*(#DIAL_VALUE#)\s*)(<\/\1>)/gm, `<$1 data-xmpie-value="${currentValue}" $2${currentValue}$5`)
            .replace(/#DIAL_VALUE#/gm, `<span data-xmpie-value="${currentValue}">${currentValue}</span>`)
        }
      }
    }
    run()
    window[callbackFunctionName] = (pValue) => {
      const valueByType = checkTypeAndConvert(pValue, type)
      runByFeature(features, {
        newTimezoneModel: () => {
          if (features.newHTMLGenericChangeValue) {
            window.GenericHtmlQueueExecutor.registerOnChange((value) => {
              clearTimeout(changeTimeout.current)
              changeTimeout.current = setTimeout(() => {
                  window.triggerFormPropertyChange(id, value)
                }, 750)
            })
            window.GenericHtmlQueueExecutor.add(id, valueByType)
          } else {
            window.GenericHtmlQueueExecutor.registerOnChange((value) => {
              onChange(value)
            })
            window.GenericHtmlQueueExecutor.add(id, valueByType)
          }
        },
        newHTMLGenericChangeValue: () => {
          clearTimeout(changeTimeout.current)
          changeTimeout.current = setTimeout(() => {
            window.triggerFormPropertyChange(id, valueByType)
          }, 750)
        },
        otherwise: () => onChange(valueByType)
      })
      setValue(valueByType)
    }
    return () => {
      window.DialValues && delete window.DialValues[id]
      delete window[callbackFunctionName]
      window.GenericHtmlQueueExecutor = new GenericHtmlQueueExecutor()
    }
  },[])

  useEffect(() => {
    const newValue = value || ''
    window.DialValues = window.DialValues || {}
    window.DialValues[id] = newValue

    container.current.querySelectorAll('[data-xmpie-value]').forEach((e) => {
      if (e.nodeName === 'INPUT') {
        e.value = newValue
      } else {
        e.innerHTML = newValue
      }
    })
  }, [value])

  return (
    <div className='html-generic-duc-wrapper'>
      <div className="generic-inner" ref={container} />
    </div>
  )
}

if (typeof window !== 'undefined') {
  window.uStoreDucs = window.uStoreDucs || []
  window.uStoreDucs.push({ name: 'htmlGeneric', component: HtmlGeneric, displayName: 'Generic HTML' })
}

export default HtmlGeneric

/*******
 * The following code added from V14.1 for backward compatibility
 */

class GenericHtmlQueueExecutor {
  constructor () {
    this.changeAccumolator = {}
    this.onChange = null
  }

  registerOnChange(onChange) {
    this.onChange = this.onChange || onChange
  }

  init = debounce(100,() => {
    this.send()
  }, { atBegin: false })

  add(id, value)  {
    this.changeAccumolator[id] = value
    this.init()
  }

  send() {
    if (Object.keys(this.changeAccumolator).length > 0) {
      this.onChange(JSON.stringify({'__GENERIC_HTML_ACCUMULATOR_VALUE__': {...this.changeAccumolator}}))
      this.changeAccumolator = {}
    }
  }
}

window.GenericHtmlQueueExecutor = window.GenericHtmlQueueExecutor || new GenericHtmlQueueExecutor()

/**
 * End of code added for backward compatibility
 */
